import { AnalyticsEvent, AnalyticsEventProperties } from '../types';

import { eventsQueue } from './storage';

/**
 * Dispatches a batch of events via fetch with `keepalive: true`. Compared to
 * the older `navigator.sendBeacon` path this gives us:
 *   - Per-origin quotas decoupled from the sendBeacon pool.
 *   - Standard fetch semantics: explicit `Content-Type`, error capture via
 *     `.catch`, request shows up in observability dashboards.
 *   - `priority: 'low'` so analytics never competes with payment-critical
 *     requests for the browser's resource scheduler.
 */
function dispatchBatch(
  url: string,
  batch: AnalyticsEvent<AnalyticsEventProperties>[],
): void {
  // Batches reach this point with events that have already been successfully
  // serialized in `queueEvent`, so `JSON.stringify` here can't throw.
  void fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(batch),
    keepalive: true,
    priority: 'low',
  }).catch((error) => {
    console.warn('Unable to send analytics events', error);
  });
}

const FLUSH_DEBOUNCE_MS = 500;
const MAX_PAYLOAD_SIZE = 64000; // 64KB — keepalive request body cap.

// Per-URL debounce timer. Usually one entry — the analytics URL is fixed
// per build — but keyed by URL so a brief `defaultUrl` queue (before the
// clientToken is decoded) doesn't share state with the real URL's timer.
const pendingFlushTimers = new Map<string, number>();

export function sendEvents(url: string, immediate = false) {
  // A debounce timer is already pending — let it fire and skip allocating
  // a new closure. Immediate mode skips this; it cancels the timer below.
  if (!immediate && pendingFlushTimers.has(url)) {
    return;
  }

  const send = () => {
    pendingFlushTimers.delete(url);
    const events = eventsQueue().get(url);
    if (!events?.length) return;

    let batch: AnalyticsEvent<AnalyticsEventProperties>[] = [];
    let batchSize = 0;

    for (const event of events) {
      const eventSize = new Blob([JSON.stringify(event)]).size;

      if (batchSize + eventSize > MAX_PAYLOAD_SIZE && batch.length > 0) {
        dispatchBatch(url, batch);
        batch = [];
        batchSize = 0;
      }

      batch.push(event);
      batchSize += eventSize;
    }

    if (batch.length > 0) {
      dispatchBatch(url, batch);
    }

    // Assume keepalive commits to dispatch; clear the queue.
    eventsQueue().delete(url);
  };

  if (immediate) {
    // Cancel any pending debounce — we're sending now.
    const pending = pendingFlushTimers.get(url);
    if (pending !== undefined) {
      clearTimeout(pending);
      pendingFlushTimers.delete(url);
    }
    send();
    return;
  }

  const id = setTimeout(send, FLUSH_DEBOUNCE_MS) as unknown as number;
  pendingFlushTimers.set(url, id);
}

export function queueEvent(
  url: string,
  event: AnalyticsEvent<AnalyticsEventProperties>,
) {
  // Serialize once at the door:
  //   - catches circular references / BigInts before they poison later flushes,
  //   - measures the size so we can drop oversized events (single events
  //     larger than the keepalive cap can never be sent).
  let eventSize: number;
  try {
    eventSize = new Blob([JSON.stringify(event)]).size;
  } catch (error) {
    console.warn('Failed to serialize analytics event', error);
    return;
  }
  if (eventSize > MAX_PAYLOAD_SIZE) {
    console.error(
      'Event size exceeds maximum payload size and will be skipped:',
      event,
    );
    return;
  }

  if (!eventsQueue().has(url)) eventsQueue().set(url, []);
  const queue = eventsQueue().get(url);
  queue?.push(event);

  sendEvents(url);
}
